// ---------------------------------------------------------------------------
// Finance guides - the money lane.
//
// These assume the setup lane already happened: billing centre, tax and invoice
// numbering exist, and somebody is already selling. What is left is the monthly
// rhythm: raise, send, collect, renew, and check the numbers.
//
// Two permission traps are baked into the ids below and mirrored in
// journeys.py. Payment management, receipts and the collection register are
// gated on `financials.view`, not `billing.manage`, so a user holding only
// billing.manage opens none of them. And the renewals page needs both
// `subscriptions.view` to open and `renewals.manage` for the wizard button.
//
// Never write a currency symbol into this copy. Currency is per org, and these
// guides are read by operators in several countries.
// ---------------------------------------------------------------------------

import { PATH_ADMIN } from '../../routes/paths';
import { ANCHOR, CENTER } from '../anchors';
import { COACH_EVENT } from '../events';

const INVOICES = PATH_ADMIN.orderManagement.invoiceList;
const PAYMENTS = PATH_ADMIN.orderManagement.paymentManagement;
const RENEWALS = PATH_ADMIN.members.autoRenewals;
const REPORTS = PATH_ADMIN.reports.root;

export const FINANCE_INVOICES = {
  id: 'finance-invoices',
  title: 'Work the invoice list',
  summary:
    'Read what the statuses actually mean, find the invoice you are being asked '
    + 'about, and know when to credit note instead of edit.',
  category: 'finance',
  level: 'advanced',
  permission: 'billing.view',
  featureSlug: null,
  estMinutes: 5,
  readinessKey: null,
  keywords: [
    'invoice', 'invoices', 'bill', 'billing', 'credit note', 'overdue',
    'unpaid', 'send invoice', 'invoice status', 'gst',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: INVOICES,
      target: CENTER,
      placement: 'center',
      title: 'Every amount you have ever asked for is on this page',
      body:
        'Almost none of it was typed by hand. Invoices fall out of '
        + 'subscriptions, renewals, room bookings and day passes. If a number '
        + 'here is wrong, the fix is nearly always upstream in the subscription '
        + 'that produced it, not in the invoice itself.',
    },
    {
      id: 'statuses',
      kind: 'narrate',
      route: INVOICES,
      target: CENTER,
      placement: 'center',
      title: 'Four states, and only one of them is your problem',
      body:
        'Draft is not owed by anyone yet. Sent is owed and inside its due date. '
        + 'Overdue is owed and late, and that is your working list. Paid is '
        + 'closed. A part-paid invoice still reads as unpaid, because a customer '
        + 'who paid half still owes you the other half, and the payment history '
        + 'on the invoice is where you see what actually landed.',
    },
    {
      id: 'finding',
      kind: 'narrate',
      route: INVOICES,
      target: CENTER,
      placement: 'center',
      title: 'Finding the one you are being asked about',
      body:
        'Filter by company when someone calls, by status when you are chasing, '
        + 'and by date range when you are closing a month. Search takes the '
        + 'invoice number straight. Open one to see the lines, the tax breakdown, '
        + 'what has been paid against it, and every time it was emailed.',
    },
    {
      id: 'sending',
      kind: 'narrate',
      route: INVOICES,
      target: CENTER,
      placement: 'center',
      title: 'Sending, and what the customer sees',
      body:
        'Sending emails a PDF to the company billing contact and gives them a '
        + 'link to pay online if a gateway is connected. If your reminders are '
        + 'set up, DeskOS chases before and after the due date on its own, so '
        + 'resend by hand only when someone tells you they never got it.',
    },
    {
      id: 'credit-notes',
      kind: 'narrate',
      route: INVOICES,
      target: CENTER,
      placement: 'center',
      title: 'When it is wrong, credit it rather than rewrite it',
      body:
        'A sent invoice is a tax document with a number in a legal series. '
        + 'Quietly changing one leaves your books and your customer disagreeing '
        + 'about what was owed. Raise a credit note against it and issue a fresh '
        + 'invoice instead. It takes a minute longer and it is the version your '
        + 'accountant can defend.',
    },
  ],
};

export const FINANCE_PAYMENTS = {
  id: 'finance-payments',
  title: 'Record and reconcile payments',
  summary:
    'Mark what has been received, handle part payments and TDS, and keep the '
    + 'ledger honest.',
  category: 'finance',
  level: 'advanced',
  permission: ['billing.manage', 'financials.view'],
  featureSlug: null,
  estMinutes: 6,
  readinessKey: null,
  keywords: [
    'payment', 'mark paid', 'receipt', 'collection', 'reconcile', 'tds',
    'advance', 'deposit', 'part payment', 'bank',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: PAYMENTS,
      target: CENTER,
      placement: 'center',
      title: 'Online payments land here on their own',
      body:
        'If a customer pays through the link on their invoice, DeskOS records it '
        + 'and closes the invoice without you. This page is for everything else: '
        + 'bank transfers, cheques, cash at the desk. What you are doing here is '
        + 'telling the system about money that arrived somewhere it cannot see.',
    },
    {
      id: 'mark-paid',
      kind: 'await',
      route: PAYMENTS,
      target: CENTER,
      placement: 'center',
      title: 'Record one against an invoice',
      body:
        'Open an outstanding invoice and mark the payment. Record the real '
        + 'amount that hit your bank rather than the invoice total, because a '
        + 'part payment recorded as a full one is a debt you will never chase. '
        + 'Set the date to when the money actually arrived, not today. And a '
        + 'payment can be split across cash, an advance the customer already '
        + 'holds, a deposit adjustment and TDS deducted at source, which is how '
        + 'you make the lines add up to what the bank shows.',
      waitingLabel: 'Waiting for a payment to be recorded.',
      advanceOn: [{ type: 'event', name: COACH_EVENT.paymentRecorded }],
      allowSkip: true,
    },
    {
      id: 'receipt',
      kind: 'narrate',
      route: PAYMENTS,
      target: CENTER,
      placement: 'center',
      title: 'That produced a receipt',
      body:
        'Every payment you record raises a receipt the customer can be sent, and '
        + 'each one is a separate row rather than an edit to the last one. So an '
        + 'invoice paid in three instalments has three receipts against it, and '
        + 'that trail is what you show when somebody disputes what they paid.',
    },
    {
      id: 'ledger',
      kind: 'narrate',
      route: PAYMENTS,
      target: CENTER,
      placement: 'center',
      title: 'Advances, deposits and the statement of account',
      body:
        'Money can arrive before there is anything to bill it against. Record it '
        + 'as an advance and it sits on the company as credit until you apply it '
        + 'to an invoice. Each company has a statement of account on its '
        + 'Financials tab showing every invoice, receipt, credit note and advance '
        + 'in order with a running balance. When a customer says "we already '
        + 'paid", that page is the answer.',
    },
    {
      id: 'bank',
      kind: 'narrate',
      route: PAYMENTS,
      target: CENTER,
      placement: 'center',
      title: 'And once a month, reconcile against the bank',
      body:
        'You can import a bank statement and let DeskOS match the lines to '
        + 'invoices, receipts and advances, including where TDS was deducted. It '
        + 'is the only reliable way to catch the payment that arrived but was '
        + 'never recorded, which is the single most common reason a customer gets '
        + 'chased for money they already sent.',
    },
  ],
};

export const FINANCE_RENEWALS = {
  id: 'finance-renewals',
  title: 'Run the renewal cycle',
  summary:
    'See what is expiring, raise the next period in a batch, and handle the ones '
    + 'that are changing.',
  category: 'finance',
  level: 'advanced',
  permission: ['subscriptions.view', 'renewals.manage'],
  featureSlug: null,
  estMinutes: 6,
  readinessKey: null,
  keywords: [
    'renewal', 'renew', 'expiring', 'notice', 'churn', 'renewal wizard',
    'next month', 'extend membership',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: RENEWALS,
      target: CENTER,
      placement: 'center',
      title: 'This is the most valuable page in DeskOS',
      body:
        'Nearly all of your revenue next month is already sitting in this list. '
        + 'A membership that lapses because nobody looked is a desk that stops '
        + 'earning while still costing you rent, and it is far more expensive '
        + 'than any lead you failed to close. Work this list on a fixed day every '
        + 'month.',
    },
    {
      id: 'wizard',
      kind: 'act',
      route: RENEWALS,
      target: ANCHOR.renewalsWizard,
      placement: 'bottom',
      title: 'Open the renewal wizard',
      body:
        'The wizard takes every expiring membership together instead of making '
        + 'you reopen each subscription one at a time. It is the difference '
        + 'between an afternoon and twenty minutes.',
      waitingLabel: 'Click the renewal wizard button.',
      advanceOn: [{ type: 'click', target: ANCHOR.renewalsWizard }],
      doItForMe: { type: 'click', target: ANCHOR.renewalsWizard },
      allowSkip: true,
    },
    {
      id: 'decide',
      kind: 'narrate',
      target: CENTER,
      placement: 'center',
      title: 'Three answers, per membership',
      body:
        'Renew as is, renew with changes, or let it end. Changes usually mean '
        + 'seat count or price: a company growing takes more desks, and an annual '
        + 'increase goes on here. Before you commit a price change, check what '
        + 'they were actually invoiced over the last few months, because the '
        + 'headline plan price and what this customer really pays are often not '
        + 'the same number.',
    },
    {
      id: 'seats',
      kind: 'narrate',
      target: CENTER,
      placement: 'center',
      title: 'Seats are checked against the future, not today',
      body:
        'A renewal reserves seats for a period that has not started yet, so '
        + 'availability is worked out for that period rather than for this '
        + 'morning. If a seat you want is already promised to somebody else '
        + 'then, DeskOS will say so and offer you a replacement rather than '
        + 'double-selling the desk.',
    },
    {
      id: 'notice',
      kind: 'narrate',
      route: RENEWALS,
      target: CENTER,
      placement: 'center',
      title: 'And the ones that are leaving',
      body:
        'A membership on notice still occupies its seats until the day it ends, '
        + 'so your occupancy stays honest right up to the handover. Mark leavers '
        + 'as soon as you know rather than at the end, because that is what gives '
        + 'your sales side a real date to sell the desk from.',
    },
  ],
};

export const FINANCE_REPORTS = {
  id: 'finance-reports',
  title: 'Read the numbers',
  summary:
    'Which report answers which question, and what MRR in DeskOS actually '
    + 'counts.',
  category: 'finance',
  level: 'advanced',
  permission: 'reports.view',
  featureSlug: null,
  estMinutes: 4,
  readinessKey: null,
  keywords: [
    'report', 'reports', 'mrr', 'revenue', 'occupancy', 'collection',
    'outstanding', 'export', 'analytics',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'narrate',
      route: REPORTS,
      target: CENTER,
      placement: 'center',
      title: 'Reports answer questions, so start with the question',
      body:
        'How full are we, how much did we invoice, how much of it came in, and '
        + 'what is still outstanding. Almost every report here is one of those '
        + 'four with a different slice on it. Pick the question first and the '
        + 'right report is usually obvious.',
    },
    {
      id: 'mrr',
      kind: 'narrate',
      route: REPORTS,
      target: CENTER,
      placement: 'center',
      title: 'What MRR means here, precisely',
      body:
        'MRR in DeskOS is invoiced revenue before tax, spread across the period '
        + 'it covers, with credit notes netted off. So a six month membership '
        + 'invoiced up front contributes its monthly share to each of those six '
        + 'months rather than spiking one of them. That is why this number will '
        + 'not match your bank statement, and why it should not.',
    },
    {
      id: 'occupancy',
      kind: 'narrate',
      route: REPORTS,
      target: CENTER,
      placement: 'center',
      title: 'Occupancy counts what is sold, not who turned up',
      body:
        'A seat is occupied when a subscription holds it, including through a '
        + 'notice period. Nobody physically sitting there does not make it '
        + 'vacant, and that is deliberate: you are being paid for it. If you want '
        + 'to know how busy the floor actually feels, that is the check-in data, '
        + 'which is a separate question.',
    },
    {
      id: 'collection',
      kind: 'narrate',
      route: REPORTS,
      target: CENTER,
      placement: 'center',
      title: 'Invoiced and collected are different numbers',
      body:
        'The gap between them is your receivables, and watching it move month to '
        + 'month tells you more about the health of the business than either '
        + 'number alone. A rising gap means you are selling well and collecting '
        + 'badly, which is the failure mode that quietly kills workspaces.',
    },
    {
      id: 'export',
      kind: 'narrate',
      route: REPORTS,
      target: CENTER,
      placement: 'center',
      title: 'Everything can leave the building',
      body:
        'Reports export, so month-end handover to an accountant is a download '
        + 'rather than a copy-paste job. If you use Tally, Xero or QuickBooks, '
        + 'there are direct integrations under Settings, and they push what you '
        + 'have here instead of asking you to key it in twice.',
    },
  ],
};

export default [
  FINANCE_INVOICES,
  FINANCE_PAYMENTS,
  FINANCE_RENEWALS,
  FINANCE_REPORTS,
];
